import { createGtm } from "@gtm-support/vue-gtm";
import { createHead } from "@unhead/vue";
import { DefaultApolloClient } from "@vue/apollo-composable";
import { createApp, h, provide, nextTick } from "vue";
import VueDOMPurifyHTML from "vue-dompurify-html";
import { getEpParam, isPreviewMode as isPageBuilderPreviewMode } from "@/builder-preview/utils";
import { apolloClient, getStore } from "@/core/api/graphql";
import { convertBrlCartsToUsd } from "@/core/api/graphql/cart/mutations/mergeCart/convertBrlCartsToUsd";
import {
  useAuth,
  useCurrency,
  useThemeContext,
  useGoogleAnalytics,
  useWhiteLabeling,
  useGeolocation,
  useDataAreaId,
} from "@/core/composables";
import { useBrlCurrencyFlagWatcher } from "@/core/composables/useBrlCurrencyFlagWatcher";
import { useBrlCurrencyMigration } from "@/core/composables/useBrlCurrencyMigration";
import { useHotjar } from "@/core/composables/useHotjar";
import { useLanguages } from "@/core/composables/useLanguages";
import { IS_DEVELOPMENT } from "@/core/constants";
import { setGlobals } from "@/core/globals";
import { authPlugin, configPlugin, contextPlugin, permissionsPlugin, applicationInsightsPlugin } from "@/core/plugins";
import { extractHostname, getBaseUrl, Logger } from "@/core/utilities";
import {
  LOCAL_STORAGE_CREDENTIALS_SET_FOR_PUNCHOUT_SESSION,
  punchoutCookies,
} from "@/core/utilities/punchout/punchoutCookies";
import { createI18nThorlabs } from "@/i18n";
import { createRouter } from "@/router";
import { useUser } from "@/shared/account";
import { baiduAnalyticsPlugin } from "@/shared/baidu-analytics/baidu-analytics.plugin";
import ProductBlocks from "@/shared/catalog/components/product";
import { initLivePersonTag } from "@/shared/live-person/utils/live-person-tag";
import { oneTrustPlugin } from "@/shared/onetrust/onetrust.plugin";
import { templateBlocks } from "@/shared/static-content/components/index";
import { usePostHog } from "@/shared/thorlabs/posthog/composables/usePostHog";
import { uiKit } from "@/ui-kit";
import App from "./App.vue";
import FeatureFlags from "./core/utilities/featureFlags";
import { optiPlugin } from "./shared/thorlabs/directives/epiEdit";
import { addCustomValidations } from "./shared/thorlabs/yup-schema-validations/validations";
import type { StoreResponseType } from "./core/api/graphql/types";
import type { ILivePersonConfiguration } from "@/shared/live-person/types";

// eslint-disable-next-line no-restricted-exports
export default async () => {
  const appSelector = "#app";
  const appElement = document.querySelector<HTMLElement | SVGElement>(appSelector);

  if (!appElement) {
    return Logger.debug(`The element with the selector "${appSelector}" was not found.`);
  }

  /**
   * Create and mount application
   */
  const app = createApp(
    {
      setup() {
        provide(DefaultApolloClient, apolloClient);
      },
      render: () => h(App),
    },
    {
      /**
       * Passing data-* attributes to the application props
       */
      ...appElement.dataset,
    },
  );

  app.use(authPlugin);

  const { fetchUser, user, isAuthenticated } = useUser();
  const { themeContext, fetchThemeContext } = useThemeContext();
  const { currentLocale, currentLanguage, defaultLocale, setLocale } = useLanguages();
  const { currentCurrency } = useCurrency();
  const { init: initializeGoogleAnalytics } = useGoogleAnalytics();
  const { init: initializeHotjar } = useHotjar();
  const { themePresetName, fetchWhiteLabelingSettings } = useWhiteLabeling();
  const { getCountryCode } = useGeolocation();

  // const fallback = {
  //   locale: "en",
  //   message: {},
  //   async setMessage() {
  //     this.message = await fetchLocaleMessages(this.locale);
  //   },
  // };

  // Strictly guard against a stale access token emited for punchout session, but then used in a normal session
  const { resetTokenDetails } = useAuth();
  if (localStorage.getItem(LOCAL_STORAGE_CREDENTIALS_SET_FOR_PUNCHOUT_SESSION) && !punchoutCookies.value) {
    resetTokenDetails();
  }

  const store = (await getStore(
    IS_DEVELOPMENT ? extractHostname(import.meta.env.APP_BACKEND_URL as string) : window.location.hostname,
  )) as StoreResponseType;

  if (!store) {
    throw new Error("Can't get store");
  }

  const featureFlags = new FeatureFlags(store.settings.modules);
  const optimizelyHost = featureFlags.getValue("Thorlabs.Commerce", "Optimizely.Host") as string;
  const optimizelyCdn = featureFlags.getValue("Thorlabs.Commerce", "Optimizely.CDN") as string;
  const optimizelyUrl = optimizelyCdn != null && optimizelyCdn != "" ? optimizelyCdn : optimizelyHost;
  const hCaptchaSiteKey = featureFlags.getValue("Thorlabs.Commerce", "HCaptcha.SiteKey") as string;
  const newProductsDayLimit = featureFlags.getValue("Thorlabs.Commerce", "NewProductsDayLimit") as number;
  const leafletApiKey = featureFlags.getValue("Thorlabs.Commerce", "LeafletApiKey") as string;
  const livePersonConfiguration = JSON.parse(
    (featureFlags.getValue("Thorlabs.Commerce", "EnableLiveChatOnLanguages") as string) || "{}",
  ) as Record<string, ILivePersonConfiguration>;
  const livePersonAccountId = (featureFlags.getValue("Thorlabs.Commerce", "LivePerson.AccountId") as string) || "";
  const countryCode = await getCountryCode(optimizelyHost).catch((error: unknown) => {
    Logger.error("Failed to resolve country code during app bootstrap, continuing without it", error);
    return "";
  });
  const shippingOptionsFriendlyNames = JSON.parse(
    (featureFlags.getValue("Thorlabs.Commerce", "ShippingOptionsFriendlyNames") as string) || "{}",
  ) as Record<string, string>;
  const shippingTransitTimeText = JSON.parse(
    (featureFlags.getValue("Thorlabs.Commerce", "ShippingTransitTimeText") as string) || "{}",
  ) as Record<string, string>;
  const quoteExpiration = JSON.parse(
    (featureFlags.getValue("Thorlabs.Commerce", "QuoteExpiration") as string) || "{}",
  ) as Record<string, number>;
  const sweAsMUCInEURCybersource = featureFlags.getValue("Thorlabs.Commerce", "SWEasMUCinCybersourceEUR") as boolean;
  const convertCc478OrdersToNetTerms = featureFlags.getValue(
    "Thorlabs.Commerce",
    "Convert478CCOrdersToNetTerms",
  ) as boolean;
  const eusDocuSignEnabled = featureFlags.getValue("Thorlabs.Commerce", "EusDocuSignEnabled") as boolean;
  // Store flag only (no INR allowlist): drives the India EUS DocuSign wording on order pages.
  const inrCurrencyEnabled = featureFlags.isFeatureEnabled("Thorlabs.Commerce", "EnableInrCurrency");
  const channelPartnerOrdersEnabled = featureFlags.getValue(
    "Thorlabs.Commerce",
    "ChannelPartnerOrdersEnabled",
  ) as boolean;
  const enable3DSPayerAuthentication =
    (featureFlags.getValue("Thorlabs.Commerce", "Enable3DSPayerAuthentication") as string) ?? "";
  const erpImportRequestBodyAndResponseErrorInOrderDetails = featureFlags.getValue(
    "Thorlabs.Commerce",
    "ERPImportRequestBodyAndResponseErrorInOrderDetails",
  ) as boolean;
  const endUserStatements = JSON.parse(
    (featureFlags.getValue("Thorlabs.Commerce", "EndUserStatements") as string) || "{}",
  ) as Record<string, Record<string, { pdf: string | null; docusign: string | null }>>;
  const resellerEndUserStatements = JSON.parse(
    (featureFlags.getValue("Thorlabs.Commerce", "ResellerEndUserStatements") as string) || "{}",
  ) as Record<string, Record<string, { pdf: string | null; docusign: string | null }>>;
  const heavyWeightForm = JSON.parse(
    (featureFlags.getValue("Thorlabs.Commerce", "HeavyWeightForm") as string) || "{}",
  ) as Record<string, Record<string, { pdf: string | null; docusign: string | null }>>;
  const workableJobsApiURL = featureFlags.getValue("Thorlabs.Commerce", "Workable:JobsApiURL") as string;
  const gtmContainerID = featureFlags.getValue("Thorlabs.Commerce", "GTM:ContainerID") as string;
  const baiduSiteID = featureFlags.getValue("Thorlabs.Commerce", "Baidu:SiteID") as string;
  const algoliaApplicationId = featureFlags.getValue("Thorlabs.Commerce", "Algolia:ApplicationId") as string;
  const algoliaSearchApiKey = featureFlags.getValue("Thorlabs.Commerce", "Algolia:SearchApiKey") as string;
  const algoliaIndexNamePrefix = featureFlags.getValue("Thorlabs.Commerce", "Algolia:IndexNamePrefix") as string;
  const appInsightsInstrumentationKey =
    (featureFlags.getValue("VirtoCommerce.ApplicationInsights", "ApplicationInsights.InstrumentationKey") as string) ??
    "";
  await Promise.all([fetchThemeContext(store, countryCode), fetchUser()]);
  const hadStoredBrlCurrency = useBrlCurrencyMigration().migrateStoredBrlCurrency();
  // when `Disable BRL Currency` is ON, users and guests will have their BRL carts converted to USD on page-reload
  if (hadStoredBrlCurrency && user.value.id) {
    await convertBrlCartsToUsd({
      storeId: themeContext.value.storeId,
      userId: user.value.id,
      cultureName: currentLanguage.value.cultureName,
    });
  }
  useBrlCurrencyFlagWatcher().watchBrlCurrencyFlag();
  const livePersonConfigOnCurrentLanguage = livePersonConfiguration[
    currentLanguage.value.twoLetterLanguageName
  ] as ILivePersonConfiguration;
  const oneTrustDomainScripts = JSON.parse(
    (featureFlags.getValue("Thorlabs.Commerce", "OneTrust:DomainScripts") as string) || "{}",
  ) as Record<string, string>;
  const postHogConfigJson = (featureFlags.getValue("Thorlabs.Commerce", "PostHog:Config") as string) || null;

  initLivePersonTag(livePersonAccountId);

  await initializeGoogleAnalytics();
  await initializeHotjar();

  /**
   * Creating plugin instances
   */
  const head = createHead();
  const i18n = createI18nThorlabs(currentLanguage.value.twoLetterLanguageName, currentCurrency.value.code);
  const router = createRouter({
    base: getBaseUrl(currentLocale.value, defaultLocale.value),
  });

  /**
   * Setting global variables
   */
  setGlobals({
    i18n,
    router,
    storeId: themeContext.value.storeId,
    catalogId: themeContext.value.catalogId,
    userId: user.value.id,
    isAuthenticated: isAuthenticated.value,
    organizationId: user.value?.contact?.organizationId,
    cultureName: currentLanguage.value.cultureName,
    currencyCode: currentCurrency.value.code,
    optimizelyHost: optimizelyHost,
    optimizelyCdn: optimizelyCdn,
    optimizelyUrl: optimizelyUrl,
    hCaptchaSiteKey,
    newProductsDayLimit,
    leafletApiKey,
    shippingOptionsFriendlyNames,
    shippingTransitTimeText,
    quoteExpiration,
    sweAsMUCInEURCybersource,
    convertCc478OrdersToNetTerms,
    eusDocuSignEnabled,
    inrCurrencyEnabled,
    channelPartnerOrdersEnabled,
    enable3DSPayerAuthentication,
    erpImportRequestBodyAndResponseErrorInOrderDetails,
    endUserStatements,
    resellerEndUserStatements,
    heavyWeightForm,
    workableJobsApiURL,
    algoliaConfig: {
      applicationId: algoliaApplicationId,
      searchApiKey: algoliaSearchApiKey,
      indexNamePrefix: algoliaIndexNamePrefix,
    },
    appInsightsInstrumentationKey,
    livePersonConfigOnCurrentLanguage: livePersonConfigOnCurrentLanguage,
  });

  /**
   * Other settings
   */
  await setLocale(i18n, currentLocale.value);

  await fetchWhiteLabelingSettings();

  // yup custom form fields validations
  addCustomValidations(i18n);

  if (themePresetName.value) {
    await fetchThemeContext(store, countryCode, themePresetName.value);
  }

  // Plugins
  app.use(head);
  app.use(i18n);
  app.use(router);
  app.use(permissionsPlugin);
  app.use(contextPlugin, themeContext.value);
  app.use(configPlugin, themeContext.value.settings);
  app.use(uiKit);
  app.use(optiPlugin);
  app.use(VueDOMPurifyHTML);

  // ============================================================================
  // Google Tag Manager - Custom SPA Page Tracking
  // ============================================================================
  if (gtmContainerID && !window.location.hostname.includes(".cn")) {
    app.use(
      createGtm({
        id: gtmContainerID,
        defer: true,
        enabled: true,
        debug: IS_DEVELOPMENT,
        // Don't pass vueRouter - we'll handle page tracking manually
      }),
    );

    // Tracking state
    let lastTrackedLocation = "";
    let lastTrackedTitle = "";
    let lastTrackedPath = "";

    const trackPageView = async () => {
      const currentLocation = window.location.href;
      const currentPath = window.location.pathname;

      // Avoid duplicate tracking of the same full URL
      if (currentLocation === lastTrackedLocation) {
        return;
      }

      // Skip initial tab selection (when tabName query param is first added to family pages)
      if (
        currentPath === lastTrackedPath &&
        lastTrackedLocation &&
        !lastTrackedLocation.includes("tabName=") &&
        currentLocation.includes("tabName=")
      ) {
        lastTrackedLocation = currentLocation;
        // Update GTM's built-in variables for tab_view event
        if (window.dataLayer) {
          window.dataLayer.push({
            page_location: currentLocation,
            page_path: currentPath,
          });
        }
        return;
      }

      const previousLocation = lastTrackedLocation;
      const previousTitle = lastTrackedTitle;

      // Update immediately to prevent race conditions
      lastTrackedLocation = currentLocation;
      lastTrackedPath = currentPath;

      // Wait for Vue component to mount
      await nextTick();

      // Poll for title change (max 3 seconds)
      const waitForTitleChange = (maxAttempts = 30, interval = 100): Promise<string> => {
        return new Promise((resolve) => {
          let attempts = 0;
          const startTitle = document.title;

          const checkTitle = () => {
            const currentTitle = document.title;
            attempts++;

            if ((currentTitle !== startTitle && currentTitle !== previousTitle) || attempts >= maxAttempts) {
              resolve(currentTitle);
            } else {
              setTimeout(checkTitle, interval);
            }
          };

          checkTitle();
        });
      };

      const newTitle = await waitForTitleChange();
      lastTrackedTitle = newTitle;

      // Send virtual_page_view event
      if (window.dataLayer) {
        window.dataLayer.push({
          event: "virtual_page_view",
          page_title: newTitle,
          page_location: currentLocation,
          page_path: window.location.pathname,
          page_referrer: previousLocation || document.referrer,
        });
      }
    };

    // Track SPA navigation (initial page load handled by GA4 Enhanced Measurement)
    router.afterEach(() => {
      void trackPageView();
    });
  }

  // ============================================================================
  // Baidu Tracking
  // ============================================================================
  if (baiduSiteID && window.location.hostname.includes(".cn")) {
    app.use(baiduAnalyticsPlugin, { siteId: baiduSiteID });

    router.afterEach((to) => {
      if (window._hmt) {
        window._hmt.push(["_trackPageview", to.fullPath]);
      }
    });
  }

  // ============================================================================
  // OneTrust Cookie Consent
  // ============================================================================
  app.use(oneTrustPlugin, { domainScripts: oneTrustDomainScripts });

  // ============================================================================
  // Application Insights (deferred until OneTrust performance/analytics consent - see plugin)
  // ============================================================================
  app.use(applicationInsightsPlugin, { instrumentationKey: appInsightsInstrumentationKey, router });

  // ============================================================================
  // PostHog A/B Testing
  // ============================================================================
  const { init: initPostHog } = usePostHog();
  initPostHog(postHogConfigJson);

  const builderOrigin = getEpParam();
  if (builderOrigin && isPageBuilderPreviewMode(builderOrigin)) {
    const builderPreviewPlugin = (await import("@/builder-preview/builder-preview.plugin").catch(Logger.error))
      ?.default;
    if (builderPreviewPlugin) {
      app.use(builderPreviewPlugin, { router, builderOrigin });
    }
  }

  // Register Page builder components globally
  Object.entries(templateBlocks).forEach(([name, component]) => app.component(name, component));

  // Register Page builder product components globally
  Object.entries(ProductBlocks).forEach(([name, component]) => app.component(name, component));

  await router.isReady();

  const { dataAreaId } = useDataAreaId();
  if (IS_DEVELOPMENT) {
    Logger.info("dataAreaId", { value: dataAreaId.value });
  }
  app.mount(appElement);

  if (!IS_DEVELOPMENT && window.self === window.top) {
    const script = document.createElement("script");
    script.src = "https://cdn.ywxi.net/js/1.js";
    script.async = true;
    script.setAttribute("data-loaded-by", "trustedsite-loader");
    document.body.appendChild(script);

    // The trustmark sets its own inline z-index, so it can only be overridden inline.
    const updateTrustedSiteZIndex = (): boolean => {
      const trustedSiteImage = document.querySelector<HTMLImageElement>("#trustedsite-tm-image");
      if (trustedSiteImage) {
        trustedSiteImage.style.setProperty("z-index", "10", "important");
        return true;
      }
      return false;
    };

    if (!updateTrustedSiteZIndex()) {
      const trustedSiteObserver = new MutationObserver(() => {
        if (updateTrustedSiteZIndex()) {
          trustedSiteObserver.disconnect();
        }
      });
      trustedSiteObserver.observe(document.body, { childList: true, subtree: true });
    }
  }
};
